import React, { useState } from 'react'
import { useCallStore } from '@/stores/callStore'
import { useUIStore } from '@/stores/uiStore'
import { apiClient } from '@/lib/api'
import { nextMorningInTimezone } from '@/lib/datetime'
import { Moon, Loader2, X } from 'lucide-react'
import { BottomSheet } from '@/components/ui/BottomSheet'

// AC-14: No outbound call or outbound SMS may initiate between 9:00 PM and
// 8:00 AM in the target contact's local time zone. A blocking dialog offers
// schedule-for-morning or manual-override-with-reason.

type View = 'choose' | 'override'

function formatLocalTime(hour: number | undefined, timezone: string | undefined): string {
  if (typeof hour !== 'number') return timezone ?? 'their local time'
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const twelve = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${twelve}:00 ${suffix}${timezone ? ` (${timezone})` : ''}`
}

export function QuietHoursModal() {
  const blockedCall = useCallStore((s) => s.blockedCall)
  const clearBlockedCall = useCallStore((s) => s.clearBlockedCall)
  const initiateCall = useCallStore((s) => s.initiateCall)
  const addToast = useUIStore((s) => s.addToast)

  const [view, setView] = useState<View>('choose')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const isOpen = !!blockedCall && blockedCall.reason === 'quiet_hours'

  const reset = () => {
    setView('choose')
    setReason('')
    setSaving(false)
  }

  const close = () => {
    reset()
    clearBlockedCall()
  }

  const handleScheduleForMorning = async () => {
    if (!blockedCall) return
    setSaving(true)
    // The quiet-hours pre-check (AC-14) blocked this call based on the
    // CONTACT's local clock, so the callback must land at 8 AM in the
    // contact's timezone too. nextMorningInTimezone picks the contact-zone
    // calendar date (today if it's before 8 AM there, otherwise tomorrow)
    // and converts that wall-clock 08:00 to an absolute UTC instant using
    // the zone's offset ON THAT DATE. The old code parsed the contact-zone
    // date string with the local-tz `new Date(...)` constructor, which
    // produced 8 AM in the USER's timezone instead.
    const tz = typeof blockedCall.details?.timezone === 'string' ? blockedCall.details.timezone : 'UTC'
    const dueDate = nextMorningInTimezone(tz, 8)

    const contactName = blockedCall.contactId
      ? `contact ${blockedCall.contactId}`
      : blockedCall.phone
    try {
      // No inner .catch here: if the task POST fails, the user must see the
      // error path below — a silent swallow used to show "Scheduled" while no
      // callback task existed, so the contact never got called back.
      await apiClient.post('/api/activities', {
        type: 'TASK',
        contactId: blockedCall.contactId,
        title: `Call ${contactName} at 8 AM`,
        dueDate: dueDate.toISOString(),
        context: blockedCall.context,
      })
      addToast({
        type: 'success',
        title: 'Scheduled for 8:00 AM',
        message: "Callback task created for the contact's local morning.",
      })
    } catch {
      addToast({
        type: 'error',
        title: 'Could not schedule task',
        message: 'Try again or use the Calendar page.',
      })
    } finally {
      setSaving(false)
      close()
    }
  }

  const handleOverride = async () => {
    if (!blockedCall) return
    if (!reason.trim()) {
      addToast({ type: 'warning', title: 'Reason required', message: 'Enter a reason before overriding.' })
      return
    }
    setSaving(true)
    const { contactId, phone, context } = blockedCall
    const overrideReason = reason.trim()
    reset()
    clearBlockedCall()
    await initiateCall({ contactId, phone, context, override: { reason: overrideReason } })
  }

  if (!isOpen || !blockedCall) return null

  const localTime = formatLocalTime(
    typeof blockedCall.details?.localHour === 'number' ? (blockedCall.details.localHour as number) : undefined,
    typeof blockedCall.details?.timezone === 'string' ? (blockedCall.details.timezone as string) : undefined,
  )

  return (
    <BottomSheet
      open={isOpen}
      onClose={close}
      title="Quiet hours"
    >
      <div style={{ padding: '0 0 16px' }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--r-sm)',
              background: 'rgb(var(--fg-rgb) / 0.06)',
              border: '0.5px solid rgb(var(--fg-rgb) / 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Moon style={{ width: 18, height: 18, color: 'rgb(var(--fg-rgb) / 0.70)' }} />
          </div>
          <div>
            <p style={{ fontSize: 15, fontWeight: 600, color: 'rgb(var(--fg-rgb))', marginBottom: 2 }}>
              Quiet hours (9 PM – 8 AM)
            </p>
            <p style={{ fontSize: 13, color: 'rgb(var(--fg-rgb) / 0.60)', lineHeight: 1.4 }}>
              This contact is in their quiet-hours window.
            </p>
          </div>
        </div>

        {/* Info banner — neutral glass (info is not a phase) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 'var(--r-md)',
            background: 'rgb(var(--fg-rgb) / 0.06)',
            border: '0.5px solid rgb(var(--fg-rgb) / 0.12)',
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, color: 'rgb(var(--fg-rgb) / 0.80)', marginBottom: 2 }}>
              Target:{' '}
              <span style={{ fontVariantNumeric: 'tabular-nums', color: 'rgb(var(--fg-rgb))' }}>
                {blockedCall.phone}
              </span>
            </p>
            <p style={{ fontSize: 12, color: 'var(--midnight-text3)' }}>
              Local time: {localTime}
            </p>
          </div>
        </div>

        {view === 'choose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Schedule for morning */}
            <button
              type="button"
              onClick={handleScheduleForMorning}
              disabled={saving}
              style={{
                textAlign: 'left',
                padding: '14px 16px',
                borderRadius: 'var(--r-md)',
                border: '0.5px solid rgb(var(--fg-rgb) / 0.20)',
                background: 'rgb(var(--fg-rgb) / 0.08)',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                touchAction: 'manipulation',
                transition: 'all 0.2s ease',
              }}
              onPointerDown={(e) => {
                if (!saving) (e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.12)')
              }}
              onPointerUp={(e) => {
                (e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.08)')
              }}
              onPointerLeave={(e) => {
                (e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.08)')
              }}
            >
              <p style={{ fontSize: 14, fontWeight: 600, color: 'rgb(var(--fg-rgb))', marginBottom: 4 }}>
                {saving ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                    Scheduling…
                  </span>
                ) : (
                  'Schedule for 8 AM'
                )}
              </p>
              <p style={{ fontSize: 12, color: 'var(--midnight-text3)', lineHeight: 1.4 }}>
                Create a callback task for the contact's local morning.
              </p>
            </button>

            {/* Override with reason */}
            <button
              type="button"
              onClick={() => setView('override')}
              style={{
                textAlign: 'left',
                padding: '14px 16px',
                borderRadius: 'var(--r-md)',
                border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
                background: 'rgb(var(--fg-rgb) / 0.04)',
                cursor: 'pointer',
                touchAction: 'manipulation',
                transition: 'all 0.2s ease',
              }}
              onPointerDown={(e) => (e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.08)')}
              onPointerUp={(e) => (e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.04)')}
              onPointerLeave={(e) => (e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.04)')}
            >
              <p style={{ fontSize: 14, fontWeight: 600, color: 'rgb(var(--fg-rgb))', marginBottom: 4 }}>
                Override with reason
              </p>
              <p style={{ fontSize: 12, color: 'var(--midnight-text3)', lineHeight: 1.4 }}>
                Call anyway. The reason you give is audit-logged.
              </p>
            </button>
          </div>
        )}

        {view === 'override' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label
                style={{ display: 'block', fontSize: 12, color: 'var(--midnight-text3)', marginBottom: 6 }}
              >
                Reason for override
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Urgent callback requested by customer"
                rows={4}
                disabled={saving}
                style={{
                  width: '100%',
                  borderRadius: 'var(--r-md)',
                  border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
                  background: 'rgb(var(--fg-rgb) / 0.04)',
                  padding: '10px 12px',
                  color: 'rgb(var(--fg-rgb))',
                  // 16px floor — iOS Safari auto-zooms <16px on focus.
                  fontSize: 16,
                  resize: 'none',
                  boxSizing: 'border-box',
                  lineHeight: 1.4,
                  opacity: saving ? 0.6 : 1,
                }}
              />
              <p style={{ fontSize: 12, color: 'rgb(var(--fg-rgb) / 0.30)', marginTop: 4 }}>
                This reason is audit-logged.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setView('choose')}
                disabled={saving}
                style={{
                  flex: 1,
                  height: 48,
                  borderRadius: 'var(--r-pill)',
                  border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
                  background: 'rgb(var(--fg-rgb) / 0.04)',
                  color: 'rgb(var(--fg-rgb) / 0.60)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  touchAction: 'manipulation',
                }}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleOverride}
                disabled={saving || !reason.trim()}
                style={{
                  flex: 1,
                  height: 48,
                  borderRadius: 'var(--r-pill)',
                  border: '0.5px solid transparent',
                  background: saving || !reason.trim()
                    ? 'rgb(var(--fg-rgb) / 0.04)'
                    : 'rgb(var(--fg-rgb))',
                  color: saving || !reason.trim() ? 'rgb(var(--fg-rgb) / 0.22)' : 'rgb(var(--bg-rgb))',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: saving || !reason.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  touchAction: 'manipulation',
                }}
              >
                {saving && <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />}
                Override & call
              </button>
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  )
}
